/**
 * Validadores centralizados para IDs de operações.
 * 
 * Este módulo fornece funções reutilizáveis para validação de IDs de pedidos (orcId, oitId)
 * garantindo consistência em todo o sistema.
 */

/**
 * Resultado da validação de IDs de pedido
 */
export interface OrderIdsValidation {
  /** Se orcId é válido (number > 0) */
  isValidOrcId: boolean;
  /** Se oitId é válido (number > 0) */
  isValidOitId: boolean;
  /** Se ambos os IDs são válidos */
  areBothValid: boolean;
  /** Operation ID gerado (formato: save-{orcId}-{oitId}) ou null se inválido */
  operationId: string | null;
}

/**
 * Valida IDs de pedido e retorna informações de validação.
 * 
 * @param orcId - ID do pedido/orçamento
 * @param oitId - ID do item do pedido
 * @returns Objeto com resultados da validação
 * 
 * @example
 * ```typescript
 * const { areBothValid, operationId } = validateOrderIds(123, 456);
 * if (areBothValid) {
 *   abortManager.registerOperation(operationId!);
 * }
 * ```
 */
export function validateOrderIds(orcId: unknown, oitId: unknown): OrderIdsValidation {
  const isValidOrcId = typeof orcId === 'number' && !isNaN(orcId) && orcId > 0;
  const isValidOitId = typeof oitId === 'number' && !isNaN(oitId) && oitId > 0;
  const areBothValid = isValidOrcId && isValidOitId;
  
  const operationId = areBothValid ? `save-${orcId}-${oitId}` : null;
  
  return {
    isValidOrcId,
    isValidOitId,
    areBothValid,
    operationId,
  };
}

/**
 * Verifica se um ID de pedido (orcId ou oitId) é válido.
 * 
 * @param id - ID a ser validado
 * @returns true se o ID é um number válido > 0
 * 
 * @example
 * ```typescript
 * if (isValidOrderId(pedido.orc_id)) {
 *   // ID válido, prosseguir
 * }
 * ```
 */
export function isValidOrderId(id: unknown): id is number {
  return typeof id === 'number' && !isNaN(id) && id > 0;
}

/**
 * Gera um operation ID a partir de IDs de pedido.
 * Retorna null se algum ID for inválido.
 * 
 * @param orcId - ID do pedido/orçamento
 * @param oitId - ID do item do pedido
 * @returns Operation ID (save-{orcId}-{oitId}) ou null
 * 
 * @example
 * ```typescript
 * const opId = generateOperationId(123, 456);
 * if (opId) {
 *   abortManager.registerOperation(opId);
 * }
 * ```
 */
export function generateOperationId(orcId: unknown, oitId: unknown): string | null {
  return validateOrderIds(orcId, oitId).operationId;
}

